import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Fab,
} from '@mui/material';
import {
  Phone,
  CallMade,
  CallReceived,
  CallEnd,
  Add,
  Schedule,
  PlayArrow,
  Stop,
} from '@mui/icons-material';
import { format, formatDuration, intervalToDuration } from 'date-fns';
import { callsService } from '../services/calls';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorAlert from '../components/common/ErrorAlert';

const Calls = () => {
  const { user } = useSelector((state) => state.auth);
  
  const [callLogs, setCallLogs] = useState([]);
  const [scheduledCalls, setScheduledCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [callTimer, setCallTimer] = useState(0);

  const [scheduleForm, setScheduleForm] = useState({
    lead: '',
    scheduled_time: '',
    purpose: '',
    notes: '',
  });

  useEffect(() => {
    fetchCallLogs();
    fetchScheduledCalls();
  }, [page, pageSize]);

  useEffect(() => {
    let interval;
    if (activeCall) {
      interval = setInterval(() => {
        setCallTimer((prevTimer) => prevTimer + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [activeCall]);

  const fetchCallLogs = async () => {
    try {
      setLoading(true);
      const response = await callsService.getCallLogs({
        page: page + 1,
        page_size: pageSize,
      });
      setCallLogs(response.results || response);
      setTotalCount(response.count || response.length);
    } catch (err) {
      setError('Failed to fetch call logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduledCalls = async () => {
    try {
      const response = await callsService.getScheduledCalls();
      setScheduledCalls(response.results || response);
    } catch (err) {
      console.error('Failed to fetch scheduled calls:', err);
    }
  };

  const handleScheduleCall = async () => {
    try {
      await callsService.createScheduledCall({
        ...scheduleForm,
        assigned_to: user.id,
      });
      setScheduleDialogOpen(false);
      setScheduleForm({
        lead: '',
        scheduled_time: '',
        purpose: '',
        notes: '',
      });
      fetchScheduledCalls();
    } catch (error) {
      console.error('Failed to schedule call:', error);
    }
  };

  const startCall = (phoneNumber, leadId) => {
    setActiveCall({
      phoneNumber,
      leadId,
      startTime: new Date(),
    });
    setCallTimer(0);
    
    // Initiate actual call through service
    callsService.initiateCall(phoneNumber, leadId).catch(console.error);
  };

  const endCall = () => {
    if (activeCall) {
      // Log the call
      const callData = {
        lead: activeCall.leadId,
        call_type: 'outgoing',
        phone_number: activeCall.phoneNumber,
        duration: callTimer,
        status: 'completed',
        started_at: activeCall.startTime,
        ended_at: new Date(),
      };
      
      // Here you would save the call log
      console.log('Call ended:', callData);
      
      setActiveCall(null);
      setCallTimer(0);
      fetchCallLogs();
    }
  };

  const getCallTypeIcon = (type) => {
    switch (type) {
      case 'incoming':
        return <CallReceived color="success" />;
      case 'outgoing':
        return <CallMade color="primary" />;
      case 'missed':
        return <CallEnd color="error" />;
      default:
        return <Phone />;
    }
  };

  const getCallStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'busy':
        return 'warning';
      case 'no_answer':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatCallDuration = (duration) => {
    if (!duration) return '-';
    
    const totalSeconds = duration.split(':').reduce((acc, time) => (60 * acc) + +time, 0);
    const interval = intervalToDuration({ start: 0, end: totalSeconds * 1000 });
    
    return formatDuration(interval, {
      format: ['hours', 'minutes', 'seconds'],
      zero: true,
      delimiter: ':',
    });
  };

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading && callLogs.length === 0) {
    return <LoadingSpinner message="Loading call logs..." />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <div>
          <Typography variant="h4" gutterBottom>
            Call Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track calls and manage scheduled conversations
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<Schedule />}
          onClick={() => setScheduleDialogOpen(true)}
        >
          Schedule Call
        </Button>
      </Box>

      {error && <ErrorAlert error={error} onClose={() => setError(null)} />}

      {/* Active Call */}
      {activeCall && (
        <Card sx={{ mb: 3, border: 2, borderColor: 'success.main' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6" color="success.main">
                  Call in Progress
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {activeCall.phoneNumber}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h4" color="success.main">
                  {formatTimer(callTimer)}
                </Typography>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<CallEnd />}
                  onClick={endCall}
                >
                  End Call
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        {/* Scheduled Calls */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Scheduled Calls
              </Typography>
              {scheduledCalls.length === 0 ? (
                <Typography color="text.secondary">
                  No scheduled calls
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {scheduledCalls.map((call) => (
                    <Box key={call.id} sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {call.lead_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(call.scheduled_time), 'MMM dd, yyyy HH:mm')}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {call.purpose}
                      </Typography>
                      {!call.is_completed && (
                        <Button
                          size="small"
                          startIcon={<Phone />}
                          onClick={() => startCall(call.lead.phone, call.lead.id)}
                          sx={{ mt: 1 }}
                        >
                          Start Call
                        </Button>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Call Logs */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Call History
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Lead</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography>Loading...</Typography>
                        </TableCell>
                      </TableRow>
                    ) : callLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="text.secondary">No call logs found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      callLogs.map((call) => (
                        <TableRow key={call.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getCallTypeIcon(call.call_type)}
                              {call.call_type}
                            </Box>
                          </TableCell>
                          <TableCell>{call.lead_name || '-'}</TableCell>
                          <TableCell>{call.phone_number}</TableCell>
                          <TableCell>{formatCallDuration(call.duration)}</TableCell>
                          <TableCell>
                            <Chip
                              label={call.status}
                              size="small"
                              color={getCallStatusColor(call.status)}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {format(new Date(call.started_at), 'MMM dd, HH:mm')}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => startCall(call.phone_number, call.lead?.id)}
                              color="primary"
                            >
                              <Phone />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(e) => {
                  setPageSize(parseInt(e.target.value));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50]}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Schedule Call Dialog */}
      <Dialog 
        open={scheduleDialogOpen} 
        onClose={() => setScheduleDialogOpen(false)}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Schedule Call</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Lead ID"
                fullWidth
                value={scheduleForm.lead}
                onChange={(e) => setScheduleForm({ ...scheduleForm, lead: e.target.value })}
                placeholder="Enter lead ID"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Scheduled Time"
                type="datetime-local"
                fullWidth
                value={scheduleForm.scheduled_time}
                onChange={(e) => setScheduleForm({ ...scheduleForm, scheduled_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Purpose"
                fullWidth
                value={scheduleForm.purpose}
                onChange={(e) => setScheduleForm({ ...scheduleForm, purpose: e.target.value })}
                placeholder="e.g., Follow-up call, Initial consultation"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                multiline
                rows={3}
                fullWidth
                value={scheduleForm.notes}
                onChange={(e) => setScheduleForm({ ...scheduleForm, notes: e.target.value })}
                placeholder="Additional notes for the call"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleScheduleCall}
            variant="contained"
            disabled={!scheduleForm.lead || !scheduleForm.scheduled_time || !scheduleForm.purpose}
          >
            Schedule Call
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="schedule call"
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: { xs: 'flex', md: 'none' },
        }}
        onClick={() => setScheduleDialogOpen(true)}
      >
        <Schedule />
      </Fab>
    </Box>
  );
};

export default Calls;